import { Message, randomUUID } from '@ag-ui/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCurrentBusinessId } from '@shared/util/hooks/useCurrentBusinessId';
import { CoastAskAgent } from './agui/CoastAskAgent';
import { AskScope } from './api';
import { withSummaryDirectives } from './util/withAskDirectives';

/**
 * Runs a single prompt for the summary widgets and hands back the answer as text.
 *
 * The panel's transport is reused as-is — same gateway, same `protocol: 'ag-ui'`, same prompt
 * token — but none of its conversation: a summary is one question with nothing behind it and
 * nothing to continue, so each run starts the agent over and only the finished text is wanted,
 * never the stream (see `AiSummary`, which shows no typewriter effect).
 */
export function useAskSummary() {
  const businessId = useCurrentBusinessId();
  const businessIdRef = useRef(businessId);
  businessIdRef.current = businessId;

  // The agent reads the scope at run time, and `run` sets it for the run it is starting.
  const scopeRef = useRef<AskScope>({ type: 'org' });

  const agent = useMemo(
    () =>
      new CoastAskAgent({
        getBusinessId: () => businessIdRef.current,
        getScope: () => scopeRef.current,
        decoratePrompt: withSummaryDirectives,
        // Nothing about history here: a summary is a widget rendering itself rather than a
        // conversation the user had, and unlisted is what an agent is unless it says otherwise.
      }),
    [],
  );

  // Nothing renders a summary that is still arriving, so a run outliving the widget has
  // nowhere to land.
  useEffect(() => () => agent.abortRun(), [agent]);

  /** Resolves with the summary, or undefined when the run failed or said nothing. */
  const run = useCallback(
    async (prompt: string, scope: AskScope): Promise<string | undefined> => {
      if (isNil(businessIdRef.current)) {
        return undefined;
      }
      scopeRef.current = scope;
      agent.resetConversation();
      agent.addMessage({ id: randomUUID(), role: 'user', content: prompt });

      // A run fails in two ways: `RUN_ERROR` is delivered to subscribers without throwing,
      // while transport and schema failures reject. See the same pair in `AgUiAskProvider`.
      let hasRunError = false;
      let newMessages: Message[];
      try {
        ({ newMessages } = await agent.runAgent(undefined, {
          onRunErrorEvent: () => {
            hasRunError = true;
          },
        }));
      } catch {
        return undefined;
      }
      if (hasRunError) {
        return undefined;
      }

      const answer = assistantText(newMessages);
      return isEmpty(answer) ? undefined : answer;
    },
    [agent],
  );

  return { run };
}

/**
 * The text the agent produced, as one string. The gateway opens an assistant message per step,
 * so a summary that took a lookup first arrives in more than one — joined rather than reduced
 * to the last, which would drop whatever the earlier ones said. Tool messages carry results
 * rather than prose and are not part of the answer.
 */
function assistantText(messages: Message[]): string {
  return messages
    .filter(message => message.role === 'assistant')
    .map(message => message.content?.trim() ?? '')
    .filter(content => !isEmpty(content))
    .join('\n\n');
}
